/**
 * @fileoverview Billing dashboard (MOR-438)
 *
 * Read-only overview of the billing engine: committed recurring revenue,
 * what has been billed and collected, what is still owed, and how revenue
 * splits across months, firms and plans.
 *
 * Interactions:
 * - Range filter drives the monthly revenue series (query `months` arg).
 * - Payment-status rows drill down into the full charge list for that state
 *   (`getChargesByStatus`, same bucketing as the aggregate).
 * - Firm rows drill into the firm's manage dialog.
 *
 * Charting decisions (see the dataviz method):
 * - Headline numbers are STAT TILES, not one-bar charts.
 * - Revenue over time is a stacked column by charge kind — two categorical
 *   series, the maximum this design system's `--chart-*` tokens validate for.
 * - "Revenue by firm" is a single-series horizontal bar (magnitude → one hue).
 * - Payment status is a proportional distribution bar + rows, not a donut.
 *
 * Money: revenue is EX-GST (GST is collected for the ATO, never revenue);
 * amounts owed are GST-inclusive, because that is what the firm pays.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { FunctionReturnType } from "convex/server";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/ui/composed/data-display/chart";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui/primitives/card";
import { Skeleton } from "@/ui/primitives/skeleton";
import { cn } from "@/utils/misc";
import {
  AlertTriangle,
  BarChart3,
  CalendarClock,
  ChevronRight,
  ExternalLink,
  Handshake,
  Inbox,
  Repeat,
  Wallet,
} from "lucide-react";
import {
  chargeStatusMeta,
  formatAud,
  formatAudExact,
  formatDate,
  formatPeriod,
  shortMonth,
  type ChargeStatusKey,
} from "./billingFormat";
import { EmptyState, PlanBadge, StatTile } from "./billingShared";

type Dashboard = FunctionReturnType<
  typeof api.admin.billing.getBillingDashboard
>;

// Subscription (recurring, dominant) takes the calmer teal; per-job fees are
// the warm accent. Two categorical series — the validated ceiling for this
// token set.
const revenueChartConfig = {
  subscriptionAudCents: {
    label: "Subscription",
    color: "hsl(var(--chart-2))",
  },
  perJobAudCents: { label: "Per job", color: "hsl(var(--chart-1))" },
} satisfies ChartConfig;

// Single series → one hue, no legend needed.
const firmsChartConfig = {
  billedExGstAudCents: { label: "Billed", color: "hsl(var(--chart-2))" },
} satisfies ChartConfig;

const RANGE_OPTIONS = [
  { value: "3", label: "Last 3 months" },
  { value: "6", label: "Last 6 months" },
  { value: "12", label: "Last 12 months" },
  { value: "24", label: "Last 24 months" },
];

export function BillingDashboard({
  onSelectFirm,
}: {
  onSelectFirm: (orgId: Id<"organizations">) => void;
}) {
  const [months, setMonths] = useState("12");
  const [drillStatus, setDrillStatus] = useState<ChargeStatusKey | null>(null);

  const { data, isPending } = useQuery(
    convexQuery(api.admin.billing.getBillingDashboard, {
      months: Number(months),
    }),
  );

  if (isPending || !data) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Revenue is ex-GST; amounts owed are GST-inclusive.
        </p>
        <Select value={months} onValueChange={setMonths}>
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RANGE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <StatRow
        totals={data.totals}
        planMix={data.planMix}
        attentionCount={data.attentionSubscriptions.length}
      />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
        <RevenueByMonth byMonth={data.byMonth} className="lg:col-span-3" />
        <PaymentStatus
          rows={data.paymentStatus}
          onDrill={setDrillStatus}
          className="lg:col-span-2"
        />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
        <TopFirms
          firms={data.topFirms}
          onSelectFirm={onSelectFirm}
          className="lg:col-span-3"
        />
        <OverdueList
          charges={data.overdueCharges}
          attentionSubscriptions={data.attentionSubscriptions}
          onSelectFirm={onSelectFirm}
          className="lg:col-span-2"
        />
      </div>

      {drillStatus && (
        <StatusDrilldownDialog
          status={drillStatus}
          onClose={() => setDrillStatus(null)}
          onSelectFirm={onSelectFirm}
        />
      )}
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-[104px] rounded-xl" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
        <Skeleton className="h-[340px] rounded-xl lg:col-span-3" />
        <Skeleton className="h-[340px] rounded-xl lg:col-span-2" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stat tiles
// ---------------------------------------------------------------------------

function StatRow({
  totals,
  planMix,
  attentionCount,
}: {
  totals: Dashboard["totals"];
  planMix: Dashboard["planMix"];
  attentionCount: number;
}) {
  const collectionPct =
    totals.billedExGstAudCents > 0
      ? Math.round(
          (totals.collectedExGstAudCents / totals.billedExGstAudCents) * 100,
        )
      : 0;
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
      <StatTile
        label="Monthly recurring"
        value={formatAud(totals.mrrExGstAudCents)}
        hint="Active subscriptions, ex GST"
        icon={<Repeat className="h-4 w-4" />}
      />
      <StatTile
        label="Collected"
        value={formatAud(totals.collectedExGstAudCents)}
        hint={`${collectionPct}% of ${formatAud(totals.billedExGstAudCents)} billed`}
        icon={<Wallet className="h-4 w-4" />}
      >
        <div className="mt-0.5 h-1 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-emerald-500"
            style={{ width: `${collectionPct}%` }}
          />
        </div>
      </StatTile>
      <StatTile
        label="Outstanding"
        value={formatAud(totals.outstandingAudCents)}
        hint="Issued but unpaid, inc GST"
        icon={<CalendarClock className="h-4 w-4" />}
      />
      <StatTile
        label="Overdue"
        value={formatAud(totals.overdueAudCents)}
        hint={
          totals.overdueAudCents > 0
            ? "Past due date — chase"
            : attentionCount > 0
              ? `${attentionCount} subscription${attentionCount === 1 ? "" : "s"} past due in Stripe`
              : "Nothing past due"
        }
        tone={
          totals.overdueAudCents > 0 || attentionCount > 0
            ? "warning"
            : "default"
        }
        icon={<AlertTriangle className="h-4 w-4" />}
      />
      <StatTile
        label="Affiliate fees"
        value={formatAud(totals.affiliateFeesAudCents)}
        hint={`${planMix.pilot} pilot · ${planMix.subscription} subscription · ${planMix.perJob} per job`}
        icon={<Handshake className="h-4 w-4" />}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Revenue over time
// ---------------------------------------------------------------------------

function RevenueByMonth({
  byMonth,
  className,
}: {
  byMonth: Dashboard["byMonth"];
  className?: string;
}) {
  const chartData = byMonth.map((m) => ({
    month: shortMonth(m.periodYearMonth),
    subscriptionAudCents: m.subscriptionAudCents,
    perJobAudCents: m.perJobAudCents,
  }));

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-base">Revenue by month</CardTitle>
        <p className="text-sm text-muted-foreground">
          Ex-GST billed per calendar month, split by fee type.
        </p>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <EmptyState
            icon={<BarChart3 className="h-8 w-8" />}
            title="No charges in this range"
            hint="Recorded charges will chart here by month."
          />
        ) : (
          <ChartContainer
            config={revenueChartConfig}
            className="h-[280px] w-full"
          >
            <BarChart data={chartData} accessibilityLayer maxBarSize={64}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey="month"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                width={64}
                tickFormatter={(v: number) => formatAud(v)}
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    formatter={(value) => formatAudExact(Number(value))}
                  />
                }
              />
              <ChartLegend content={<ChartLegendContent />} />
              {/* 2px surface gap between stacked segments. */}
              <Bar
                dataKey="subscriptionAudCents"
                stackId="revenue"
                fill="var(--color-subscriptionAudCents)"
                stroke="hsl(var(--background))"
                strokeWidth={2}
              />
              <Bar
                dataKey="perJobAudCents"
                stackId="revenue"
                fill="var(--color-perJobAudCents)"
                stroke="hsl(var(--background))"
                strokeWidth={2}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Payment status
// ---------------------------------------------------------------------------

/** Fixed display order — severity first, then the passive states. */
const STATUS_ORDER: ChargeStatusKey[] = [
  "overdue",
  "invoiced",
  "draft",
  "paid",
  "void",
];

function PaymentStatus({
  rows,
  onDrill,
  className,
}: {
  rows: Dashboard["paymentStatus"];
  onDrill: (status: ChargeStatusKey) => void;
  className?: string;
}) {
  const ordered = [...rows].sort(
    (a, b) =>
      STATUS_ORDER.indexOf(a.status as ChargeStatusKey) -
      STATUS_ORDER.indexOf(b.status as ChargeStatusKey),
  );
  const total = rows.reduce((sum, r) => sum + r.totalAudCents, 0);

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-base">Payment status</CardTitle>
        <p className="text-sm text-muted-foreground">
          Every charge by state, GST-inclusive. Click a state to see its
          charges.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {rows.length === 0 ? (
          <EmptyState
            icon={<Inbox className="h-8 w-8" />}
            title="No charges recorded yet"
          />
        ) : (
          <>
            {/* Proportional distribution of value across states. */}
            <div className="flex h-2.5 gap-0.5 overflow-hidden rounded-full">
              {ordered
                .filter((r) => r.totalAudCents > 0)
                .map((r) => (
                  <div
                    key={r.status}
                    className={cn(
                      "h-full min-w-[3px]",
                      chargeStatusMeta(r.status).barClass,
                    )}
                    style={{
                      flexGrow: r.totalAudCents,
                    }}
                    title={`${chargeStatusMeta(r.status).label}: ${formatAudExact(r.totalAudCents)}`}
                  />
                ))}
            </div>

            <div className="flex flex-col">
              {ordered.map((r) => {
                const meta = chargeStatusMeta(r.status);
                const share =
                  total > 0 ? Math.round((r.totalAudCents / total) * 100) : 0;
                return (
                  <button
                    key={r.status}
                    type="button"
                    onClick={() => onDrill(r.status as ChargeStatusKey)}
                    className="group -mx-2 flex items-center gap-3 rounded-lg px-2 py-2.5 text-left transition-colors hover:bg-muted/60"
                  >
                    <span
                      className={cn("h-2.5 w-2.5 rounded-full", meta.barClass)}
                    />
                    <span className="flex-1 text-sm font-medium">
                      {meta.label}
                    </span>
                    <span className="text-xs tabular-nums text-muted-foreground">
                      {r.count} {r.count === 1 ? "charge" : "charges"}
                    </span>
                    <span className="w-24 text-right text-sm font-medium tabular-nums">
                      {formatAudExact(r.totalAudCents)}
                    </span>
                    <span className="w-10 text-right text-xs tabular-nums text-muted-foreground">
                      {share}%
                    </span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground/40 transition-transform group-hover:translate-x-0.5 group-hover:text-muted-foreground" />
                  </button>
                );
              })}
            </div>

            <div className="flex items-center justify-between border-t pt-3 text-sm">
              <span className="text-muted-foreground">Total charged</span>
              <span className="font-semibold tabular-nums">
                {formatAudExact(total)}
              </span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Status drill-down
// ---------------------------------------------------------------------------

function StatusDrilldownDialog({
  status,
  onClose,
  onSelectFirm,
}: {
  status: ChargeStatusKey;
  onClose: () => void;
  onSelectFirm: (orgId: Id<"organizations">) => void;
}) {
  const { data: charges } = useQuery(
    convexQuery(api.admin.billing.getChargesByStatus, { status }),
  );
  const meta = chargeStatusMeta(status);
  const total = (charges ?? []).reduce((sum, c) => sum + c.totalAudCents, 0);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] min-w-0 overflow-y-auto overflow-x-hidden sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            {meta.label}
            {charges && (
              <Badge color={meta.color} variant="soft">
                {charges.length} {charges.length === 1 ? "charge" : "charges"} ·{" "}
                {formatAudExact(total)}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            All charges currently in this state, GST-inclusive. Click a row to
            open the firm.
          </DialogDescription>
        </DialogHeader>

        {!charges ? (
          <div className="flex flex-col gap-2 py-2">
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
          </div>
        ) : charges.length === 0 ? (
          <EmptyState
            icon={<Inbox className="h-8 w-8" />}
            title={`Nothing is ${meta.label.toLowerCase()} right now`}
          />
        ) : (
          <Table containerClassName="min-w-0">
            <TableHeader>
              <TableRow>
                <TableHead>Firm</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Period</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>{status === "paid" ? "Paid" : "Due"}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {charges.map((c) => (
                <TableRow
                  key={c._id}
                  className="cursor-pointer"
                  onClick={() => onSelectFirm(c.orgId)}
                >
                  <TableCell className="font-medium">{c.orgName}</TableCell>
                  <TableCell className="max-w-[220px]">
                    <div className="truncate text-sm">{c.description}</div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                    {formatPeriod(c.periodYearMonth)}
                  </TableCell>
                  <TableCell className="text-right font-medium tabular-nums">
                    {formatAudExact(c.totalAudCents)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                    {status === "paid" && c.paidAt
                      ? formatDate(c.paidAt)
                      : status === "overdue"
                        ? `${c.daysOverdue}d overdue`
                        : c.dueDate
                          ? formatDate(c.dueDate)
                          : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    {c.hostedInvoiceUrl && (
                      <Button variant="ghost" size="sm" asChild>
                        <a
                          href={c.hostedInvoiceUrl}
                          target="_blank"
                          rel="noreferrer"
                          title="Open Stripe invoice"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Top firms
// ---------------------------------------------------------------------------

function TopFirms({
  firms,
  onSelectFirm,
  className,
}: {
  firms: Dashboard["topFirms"];
  onSelectFirm: (orgId: Id<"organizations">) => void;
  className?: string;
}) {
  const chartData = firms.map((f) => ({
    name: f.orgName,
    orgId: f.orgId,
    billedExGstAudCents: f.billedExGstAudCents,
  }));

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-base">Revenue by firm</CardTitle>
        <p className="text-sm text-muted-foreground">
          Ex-GST billed to date. Click a row to drill in.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {chartData.length === 0 ? (
          <EmptyState
            icon={<BarChart3 className="h-8 w-8" />}
            title="No charges recorded yet"
          />
        ) : (
          <>
            <ChartContainer
              config={firmsChartConfig}
              className="w-full"
              style={{ height: Math.max(120, chartData.length * 44) }}
            >
              <BarChart
                data={chartData}
                layout="vertical"
                accessibilityLayer
                margin={{ left: 8 }}
                maxBarSize={22}
              >
                <CartesianGrid horizontal={false} strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v: number) => formatAud(v)}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tickLine={false}
                  axisLine={false}
                  width={120}
                />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      formatter={(value) => formatAudExact(Number(value))}
                    />
                  }
                />
                <Bar
                  dataKey="billedExGstAudCents"
                  fill="var(--color-billedExGstAudCents)"
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ChartContainer>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Firm</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead className="text-right">Billed</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {firms.map((f) => (
                  <TableRow
                    key={f.orgId}
                    className="group cursor-pointer"
                    onClick={() => onSelectFirm(f.orgId)}
                  >
                    <TableCell className="font-medium">{f.orgName}</TableCell>
                    <TableCell>
                      <PlanBadge plan={f.plan} />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatAudExact(f.billedExGstAudCents)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {f.outstandingAudCents > 0 ? (
                        formatAudExact(f.outstandingAudCents)
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="w-8 text-right">
                      <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground/40 transition-transform group-hover:translate-x-0.5 group-hover:text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Overdue
// ---------------------------------------------------------------------------

function OverdueList({
  charges,
  attentionSubscriptions,
  onSelectFirm,
  className,
}: {
  charges: Dashboard["overdueCharges"];
  attentionSubscriptions: Dashboard["attentionSubscriptions"];
  onSelectFirm: (orgId: Id<"organizations">) => void;
  className?: string;
}) {
  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-base">Needs attention</CardTitle>
        <p className="text-sm text-muted-foreground">
          Invoices past their due date, and subscriptions Stripe has flagged.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {/* Stripe-side trouble: dunning can fail before our 14-day invoice
            terms lapse, so these are not (yet) ledger-overdue. */}
        {attentionSubscriptions.length > 0 && (
          <div className="flex flex-col divide-y rounded-lg border border-red-200 bg-red-50/50 px-3 dark:border-red-900 dark:bg-red-950/40">
            {attentionSubscriptions.map((s) => (
              <button
                key={s.orgId}
                type="button"
                onClick={() => onSelectFirm(s.orgId)}
                className="flex items-center gap-3 py-2.5 text-left"
              >
                <AlertTriangle className="h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">
                    {s.orgName}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Subscription {s.stripeSubscriptionStatus.replace(/_/g, " ")}{" "}
                    in Stripe
                    {s.monthlyExGstAudCents
                      ? ` · ${formatAudExact(s.monthlyExGstAudCents)}/mo`
                      : ""}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground/40" />
              </button>
            ))}
          </div>
        )}
        {charges.length === 0 && attentionSubscriptions.length === 0 ? (
          <EmptyState
            icon={<Inbox className="h-8 w-8" />}
            title="Nothing needs attention"
            hint="Overdue invoices and struggling subscriptions will surface here."
          />
        ) : charges.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No invoices are past their due date.
          </p>
        ) : (
          <div className="flex flex-col divide-y">
            {charges.map((c) => (
              <div key={c._id} className="flex items-center gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">
                    {c.orgName}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {c.description}
                    {c.dueDate ? ` · due ${formatDate(c.dueDate)}` : ""}
                  </div>
                </div>
                <span className="text-sm font-medium tabular-nums">
                  {formatAudExact(c.totalAudCents)}
                </span>
                <Badge color="red" variant="soft" size="sm">
                  {c.daysOverdue}d
                </Badge>
                {c.hostedInvoiceUrl && (
                  <Button variant="ghost" size="sm" asChild>
                    <a
                      href={c.hostedInvoiceUrl}
                      target="_blank"
                      rel="noreferrer"
                      title="Open Stripe invoice"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
